import { useTeamsStore } from "@/common/store/teams.store";
import { defineStore } from "pinia";
import { Competition } from "@/common/models/competition";
import {
  fetchCompetitions,
  fetchCompetition,
  deleteCompetition,
  deleteCompetitions,
} from "@/common/services/competition.service";
import { fetchCompetitionMatches } from "@/common/services/matches.service";
import { useErrorStore } from "@/common/store/error.store";
import { useMatchesStore } from "@/common/store/matches.store";
import { removeEntity, replaceCollection, upsertEntities } from "@/common/store/utils";

interface CompetitionsState {
  ids: string[];
  entities: { [id: string]: Competition };
}

export const useCompetitionsStore = defineStore("competitions", {
  state: (): CompetitionsState => ({
    ids: [],
    entities: {},
  }),

  getters: {
    all(): Competition[] {
      const matchesStore = useMatchesStore();
      const teamsStore = useTeamsStore();
      const entities = this.ids
        .map((id) => this.entities[id])
        .map((e) => ({
          ...e,
          teams: teamsStore.all.filter((t) => (e.teams as string[]).includes(t.id)),
          matches: matchesStore.all.filter((m) => m.competition === e.id),
        }));
      return entities;
    },
    allActive(): Competition[] {
      return this.all.filter((e) => e.status === "ACTIVE");
    },
  },

  actions: {
    async fetchCompetitions(): Promise<void> {
      const matchesStore = useMatchesStore();
      const teamsStore = useTeamsStore();
      try {
        await teamsStore.fetchTeams();
        const competitions = await fetchCompetitions();
        this.fetchCompetitionsSuccess(competitions);
        const matchesReqs = competitions.map((c) => matchesStore.fetchCompetitionMatches(c));
        await Promise.all(matchesReqs);
      } catch (error) {
        this.fetchCompetitionsError(error);
      }
    },

    fetchCompetitionsSuccess(competitions: Competition[]): void {
      upsertEntities(competitions, this);
    },

    fetchCompetitionsError(err: any): void {
      const errorStore = useErrorStore();
      errorStore.throwError(`fetchCompetitionsError: ${err.toString()}`);
    },

    async fetchCompetition(id: string): Promise<void> {
      const matchesStore = useMatchesStore();
      const teamsStore = useTeamsStore();
      try {
        await teamsStore.fetchTeams();
        const competition = await fetchCompetition(id);
        const matches = await fetchCompetitionMatches(competition);
        matchesStore.fetchCompetitionMatchesSuccess(matches);
        this.fetchCompetitionSuccess(competition);
      } catch (error) {
        this.fetchCompetitionError(error);
      }
    },

    fetchCompetitionSuccess(competition: Competition): void {
      upsertEntities([competition], this);
    },

    fetchCompetitionError(err: any): void {
      const errorStore = useErrorStore();
      errorStore.throwError(`fetchCompetitionError: ${err.toString()}`);
    },

    async deleteCompetitionAction(competition: Competition): Promise<void> {
      try {
        await deleteCompetition(competition.id);
        this.deleteCompetitionSuccess(competition);
      } catch (error) {
        this.deleteCompetitionError(error);
        throw error;
      }
    },

    deleteCompetitionSuccess(competition: Competition): void {
      removeEntity(competition, this);
    },

    deleteCompetitionError(err: any): void {
      const errorStore = useErrorStore();
      errorStore.throwError(`deleteCompetitionError: ${err.toString()}`);
    },

    async deleteCompetitionsAction(): Promise<void> {
      try {
        await deleteCompetitions();
        this.deleteCompetitionsSuccess();
      } catch (error) {
        this.deleteCompetitionsError(error);
        throw error;
      }
    },

    deleteCompetitionsSuccess(): void {
      replaceCollection([], this);
    },

    deleteCompetitionsError(err: any): void {
      const errorStore = useErrorStore();
      errorStore.throwError(`deleteCompetitionsError: ${err.toString()}`);
    },
  },
});
